import React, { useState, useEffect } from 'react';
import {
	Form,
	Input,
	Button,
	message,
	Typography,
	Switch,
	Radio,
	Select,
	Alert,
	Checkbox,
} from 'antd';
import { getAllUserEventTypes } from '@/api/calendars/calcom';
import { Video } from 'lucide-react';
import ZoomLogo from '@/assets/images/calendars/zoom.png';
import GoogleMeetLogo from '@/assets/images/calendars/meet.png';
import CalComLogo from '@/assets/images/calendars/calcom.png';
import './CalendarSetupForm.scss';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from '@/utils/apiClient';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { getSelectedAgentId } from '@/store/account/selector';

const { Text } = Typography;

interface CalendarEditFormProps {
	initialValues: any;
	onFinish: (values: any) => void;
	onCancel: () => void;
}

export const CalendarEditForm: React.FC<CalendarEditFormProps> = ({
	initialValues,
	onFinish,
	onCancel,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [selectedEventLocations, setSelectedEventLocations] = useState<{ [key: number]: string[] }>(
		{},
	);
	const [ghlCalendars, setGhlCalendars] = useState<any[]>([]);
	const [isLoadingGhlCalendars, setIsLoadingGhlCalendars] = useState(false);
	const [isGohighlevelCalendar, setIsGohighlevelCalendar] = useState(false);
	const [validationAlerts, setValidationAlerts] = useState<
		{ type: 'error' | 'warning'; message: string }[]
	>([]);
	const location = Form.useWatch(['calendars', 0, 'location'], form);
	const additionalUserInfoFields =
		Form.useWatch(['calendars', 0, 'additionalUserInfoFields'], form) || [];

	useEffect(() => {
		if (form) {
			form.validateFields([['calendars', 0, 'customFields']]).catch(() => {});

			form.setFieldsValue({
				calendars: [
					{
						...form.getFieldValue('calendars')[0],
						additionalUserInfoFields:
							form.getFieldValue(['calendars', 0, 'additionalUserInfoFields']) || [],
					},
				],
			});
		}
	}, [additionalUserInfoFields, location, form]);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const getDefaultFieldsForLocation = (locationType: string) => {
		const baseFields = [
			'name',
			'firstName',
			'lastName',
			'eventDate',
			'eventTime',
			'timezone',
			'email',
		];

		// Add location-specific fields
		switch (locationType) {
			case 'attendeeAddress':
			case 'attendeeDefined':
			case 'attendeeInPerson':
				return [...baseFields, 'address'];
			case 'attendeePhone':
				return [...baseFields, 'phone'];
			default:
				return baseFields;
		}
	};

	useEffect(() => {
		form.validateFields([['calendars', 0, 'customFields']]);
	}, [location, form]);

	useEffect(() => {
		if (initialValues?.data?.calendars?.[0]) {
			const calendar = initialValues.data.calendars[0];
			setIsGohighlevelCalendar(calendar.nameOfCalendar === 'gohighlevel');

			form.setFieldsValue({
				calendars: [
					{
						apiKey: calendar.accessToken,
						scenario:
							calendar.scenario || 'Whenever the caller wants to book or schedule any meetings.',
						eventTypeId: calendar.eventTypeId,
						calendarSelected: calendar.calendarSelected,
						location: calendar.location,
						sendCallerSMSNotification: calendar.sendCallerSMSNotification !== false,
						sendOwnerSMSNotification: calendar.sendOwnerSMSNotification !== false,
						additionalUserInfoFields: calendar.additionalUserInfoFields || [],
						customFields: calendar.customFields || [],
					},
				],
			});

			if (calendar.nameOfCalendar === 'gohighlevel') {
				fetchGhlCalendars();
			} else {
				const fetchEventTypes = async () => {
					try {
						if (!calendar.accessToken) {
							console.error('No API key found in calendar data');
							message.error('Missing API key');
							return;
						}

						setLoading(true);

						const data = await getAllUserEventTypes({ apiKey: calendar.accessToken });
						console.info('Fetched event types:', data);

						if (data && data.length > 0) {
							const fetchedEventTypes = data.map((event: any) => ({
								id: event.id,
								name: event.title,
								locations: event.locations,
								bookingFields: event.bookingFields,
							}));

							// Check if current event type exists in fetched types
							const currentEventType = fetchedEventTypes.find(
								(et: any) => et.id.toString() === calendar.eventTypeId.toString(),
							);

							const alerts: { type: 'error' | 'warning'; message: string }[] = [];

							if (!currentEventType) {
								alerts.push({
									type: 'error',
									message:
										'This event type has been deleted in Cal.com. Please select a different event type or remove this configuration.',
								});
							} else {
								// Check for missing booking fields, excluding default and system fields
								const currentCustomFields = calendar.customFields || [];
								const defaultFields = getDefaultFieldsForLocation(calendar.location);
								const excludedFields = ['location', 'notes', 'guests', 'rescheduleReason'];
								const expectedCustomFields =
									currentEventType.bookingFields
										?.filter(
											(field: any) =>
												!defaultFields.includes(field.name) && !excludedFields.includes(field.name),
										)
										.map((field: any) => field.name) || [];

								const missingFields = expectedCustomFields.filter(
									(field: string) => !currentCustomFields.some((cf: any) => cf.name === field),
								);

								if (missingFields.length > 0) {
									alerts.push({
										type: 'warning',
										message: `Some required booking fields are missing in your configuration: ${missingFields.join(', ')}. This might cause booking issues.`,
									});
								}
							}

							setValidationAlerts(alerts);

							// Find the current event type and set its locations
							if (currentEventType?.locations) {
								const locations = currentEventType.locations.map((loc: any) => loc.type);
								setSelectedEventLocations({ 0: locations });
							}
						}
					} catch (error) {
						console.error('Error fetching event types:', error);
						message.error('Failed to fetch event types. Please check your API key');
					} finally {
						setLoading(false);
					}
				};

				fetchEventTypes();
			}
		}
	}, [initialValues, form]);

	const loggedInUser = useAppSelector(getLoggedInUser);
	const resellerEmailCheck = loggedInUser?.resellerId ? 'RESELLER' : 'RECEPTIONIST';

	// Add GoHighLevel calendar fetching
	const fetchGhlCalendars = async () => {
		try {
			setIsLoadingGhlCalendars(true);
			const response = await apiClient.request({
				method: 'GET',
				endPoint: '/api/v1/gohighlevel/agent/:agentId/calendars'.replace(
					':agentId',
					selectedAgentId as string,
				),
				params: { resellerEmailCheck },
			});

			if (response?.calendars && Array.isArray(response.calendars)) {
				setGhlCalendars(response.calendars);
			}
		} catch (error) {
			console.error('Error fetching GoHighLevel calendars:', error);
			message.error('Failed to fetch GoHighLevel calendars');
		} finally {
			setIsLoadingGhlCalendars(false);
		}
	};

	const handleGhlCalendarChange = (value: string) => {
		const selectedCalendar = ghlCalendars.find((cal) => cal.id === value);
		const calendarName = selectedCalendar?.name || '';
		const calendars = form.getFieldValue('calendars');
		if (calendars && calendars[0]) {
			calendars[0] = {
				...calendars[0],
				eventTypeId: value,
				calendarSelected: calendarName,
			};
			form.setFieldsValue({ calendars });
		}
	};

	const getLocationDisplayName = (locationType: string) => {
		switch (locationType) {
			case 'integrations:google:meet':
				return 'Google Meet';
			case 'integrations:zoom':
				return 'Zoom';
			case 'integrations:daily':
				return 'Cal.com';
			default:
				return locationType.split(':').pop() || locationType;
		}
	};

	const getLocationIcon = (locationType: string) => {
		switch (locationType) {
			case 'integrations:google:meet':
				return <img src={GoogleMeetLogo} alt="Google Meet" className="h-3" />;
			case 'integrations:zoom':
				return <img src={ZoomLogo} alt="Zoom" className="h-3" />;
			case 'integrations:daily':
				return <img src={CalComLogo} alt="Cal.com" className="h-3" />;
			default:
				return <Video className="w-5 h-5" />;
		}
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();

			const calendar = values.calendars[0];
			if (!calendar) {
				message.error('No calendar configuration found');
				return;
			}

			const originalCalendar = initialValues?.data?.calendars?.[0];
			if (!originalCalendar) {
				message.error('No original calendar configuration found');
				return;
			}
			if (isGohighlevelCalendar) {
				await onFinish({
					calendarType: 'gohighlevel',
					eventTypeId: calendar.eventTypeId,
					calendarSelected: calendar.calendarSelected || originalCalendar.calendarSelected || '',
					scenario: calendar.scenario,
					sendCallerSMSNotification: calendar.sendCallerSMSNotification !== false,
					sendOwnerSMSNotification: calendar.sendOwnerSMSNotification !== false,
				});
			} else {
				await onFinish({
					...calendar,
					apiKey: originalCalendar.apiKey,
					// Preserve calendarSelected for non-GHL calendars too (if it exists)
					calendarSelected: calendar.calendarSelected || originalCalendar.calendarSelected || '',
					// Ensure notifications are on by default if not explicitly set to false
					sendCallerSMSNotification: calendar.sendCallerSMSNotification !== false,
					sendOwnerSMSNotification: calendar.sendOwnerSMSNotification !== false,
					additionalUserInfoFields: calendar.additionalUserInfoFields || [],
					customFields: calendar.customFields || [],
				});
			}
		} catch (error) {
			console.error('Validation failed:', error);
			message.error('Please fill in all required fields');
		}
	};

	return (
		<div className="calendar-setup-form">
			<Form
				form={form}
				layout="vertical"
				initialValues={{
					calendars: [
						{
							sendCallerSMSNotification: true,
							sendOwnerSMSNotification: true,
							customFields: [],
							additionalUserInfoFields:
								initialValues?.data?.calendars?.[0]?.additionalUserInfoFields || [],
						},
					],
				}}
			>
				<div className="step-content">
					<Form.List name="calendars">
						{(fields) => (
							<>
								{fields.map((field, index) => (
									<React.Fragment key={field.key}>
										<div className="settings-section">
											<h3 className="section-title">Scenario</h3>
											<Text className="section-description">
												When should this calendar be used?
											</Text>

											<Form.Item
												name={[field.name, 'scenario']}
												rules={[{ required: true, message: 'Please enter a scenario' }]}
											>
												<Input
													placeholder="e.g., Whenever the caller wants to book or schedule any meetings"
													className="scenario-input"
												/>
											</Form.Item>
										</div>

										{isGohighlevelCalendar ? (
											<div className="settings-section">
												<h3 className="section-title">Calendar Selection</h3>
												<Text className="section-description">
													Select your GoHighLevel calendar
												</Text>
												<Form.Item
													name={[field.name, 'eventTypeId']}
													rules={[{ required: true, message: 'Please select a calendar' }]}
												>
													<Select
														placeholder="Select calendar"
														loading={isLoadingGhlCalendars}
														onChange={handleGhlCalendarChange}
														options={ghlCalendars.map((calendar) => ({
															value: calendar.id,
															label: `${calendar.name} (${calendar.id})`,
														}))}
														className="event-select"
													/>
												</Form.Item>
											</div>
										) : (
											<div className="settings-section">
												<h3 className="section-title">Meeting Location</h3>
												<Text className="section-description">
													Where would you like to host your meetings?
												</Text>

												{selectedEventLocations[index]?.length > 0 ? (
													<Form.Item
														name={[field.name, 'location']}
														rules={[
															{ required: true, message: 'Please select a meeting location' },
														]}
													>
														<Radio.Group className="location-options">
															{selectedEventLocations[index]?.map((locationType) => (
																<Radio.Button
																	key={locationType}
																	value={locationType}
																	className="location-button"
																>
																	<span className="location-icon">
																		{getLocationIcon(locationType)}
																	</span>
																	<span className="location-name">
																		{getLocationDisplayName(locationType)}
																	</span>
																</Radio.Button>
															))}
														</Radio.Group>
													</Form.Item>
												) : (
													<Text type="secondary">
														Please select an event type to see available locations
													</Text>
												)}
											</div>
										)}

										<Form.Item name={[field.name, 'calendarSelected']} hidden={true}>
											<Input />
										</Form.Item>

										<div className="settings-section">
											<h3 className="section-title">Notification Preferences</h3>
											<Form.Item
												name={[field.name, 'sendCallerSMSNotification']}
												valuePropName="checked"
												initialValue={true}
											>
												<div className="notification-option">
													<span>Send SMS to customers</span>
													<Switch defaultChecked />
												</div>
											</Form.Item>

											<Form.Item
												name={[field.name, 'sendOwnerSMSNotification']}
												valuePropName="checked"
												initialValue={true}
											>
												<div className="notification-option">
													<span>Send SMS to me</span>
													<Switch defaultChecked />
												</div>
											</Form.Item>
										</div>

										{/* User Data Requirements Section */}
										{!isGohighlevelCalendar && (
											<div className="settings-section">
												<h3 className="section-title">Additional User Information</h3>
												<Text className="section-description">
													Select which information users must provide when booking appointments.
												</Text>
												<Form.Item
													name={[field.name, 'additionalUserInfoFields']}
													tooltip="By default, these fields are optional for the user"
													className="mt-4"
												>
													<Checkbox.Group className="w-full">
														<div className="flex justify-between w-full field-option">
															<div className="field-info">
																<span className="font-bold text-sm">Email address</span>
																<div className="field-description">
																	Users receive booking confirmations via email
																</div>
															</div>
															<Checkbox value="email" className="field-checkbox" />
														</div>
														<div className="flex justify-between w-full field-option">
															<div className="field-info">
																<span className="font-bold text-sm">Timezone</span>
																<div className="field-description">
																	Shows appointment times in user's local timezone
																</div>
															</div>
															<Checkbox value="timezone" className="field-checkbox" />
														</div>
													</Checkbox.Group>
												</Form.Item>
											</div>
										)}

										{/* Custom Fields Section */}
										{!isGohighlevelCalendar && (
											<div className="settings-section custom-fields-section">
												<h3 className="section-title">Custom Fields</h3>
												<Alert
													type="info"
													message="Default Fields Information"
													description={(() => {
														const additionalUserInfoFieldsValue =
															form.getFieldValue(['calendars', 0, 'additionalUserInfoFields']) ||
															[];
														const defaultadditionalUserInfoFields = [
															'Name',
															'Event Date',
															'Event Time',
														];
														if (additionalUserInfoFieldsValue.includes('email')) {
															defaultadditionalUserInfoFields.push('Email');
														}
														if (additionalUserInfoFieldsValue.includes('timezone')) {
															defaultadditionalUserInfoFields.push('Timezone');
														}

														const defaultOptionalFields = [];
														if (!additionalUserInfoFieldsValue.includes('email')) {
															defaultOptionalFields.push('Email');
														}
														if (!additionalUserInfoFieldsValue.includes('timezone')) {
															defaultOptionalFields.push('Timezone');
														}

														let extraField = '';
														if (
															['attendeeAddress', 'attendeeDefined', 'attendeeInPerson'].includes(
																location,
															)
														) {
															extraField = 'Address';
														} else if (location === 'attendeePhone') {
															extraField = 'Phone';
														}
														return (
															<div className="fields-info">
																<p>
																	The following fields are already included and cannot be added as
																	custom fields:
																</p>
																<ul className="field-list">
																	<li>
																		<b>Always Required:</b>{' '}
																		{defaultadditionalUserInfoFields.join(', ')}
																	</li>
																	{defaultOptionalFields.length > 0 && (
																		<li>
																			<b>Optional:</b> {defaultOptionalFields.join(', ')}
																		</li>
																	)}
																	{extraField && (
																		<li>
																			<b>{extraField}</b> will be asked for this{' '}
																			{extraField === 'Address' ? 'in-person' : 'phone'} meeting
																			type
																		</li>
																	)}
																</ul>
																<div className="mt-2">
																	<b>Note:</b> All custom fields must also be configured with
																	matching names in Cal.com
																</div>
															</div>
														);
													})()}
													className="mb-4"
													showIcon
												/>
												{validationAlerts.length > 0 && (
													<div className="validation-alerts mb-4">
														{validationAlerts.map((alert, index) => (
															<Alert
																key={index}
																type={alert.type}
																message={alert.message}
																className="mb-2"
																showIcon
															/>
														))}
													</div>
												)}
												<Text className="section-description mb-3">
													Collect additional information from users during scheduling
												</Text>

												<Form.List name={[field.name, 'customFields']}>
													{(fields, { add, remove }) => (
														<div className="custom-fields-container">
															{fields.length > 0 ? (
																<div className="custom-fields-list">
																	{fields.map(({ key, name, ...restField }) => (
																		<div
																			key={key}
																			className="custom-field-row grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center"
																		>
																			<Form.Item
																				{...restField}
																				name={[name, 'name']}
																				rules={[
																					{ required: true, message: 'Field name is required' },
																					{
																						validator: (_, value) => {
																							const normalizedValue = value?.toLowerCase().trim();
																							const currentLocation = form.getFieldValue([
																								'calendars',
																								0,
																								'location',
																							]);
																							const defaultFields =
																								getDefaultFieldsForLocation(currentLocation);

																							if (defaultFields.includes(normalizedValue)) {
																								return Promise.reject(
																									new Error(
																										'This field is already included by default',
																									),
																								);
																							}

																							if (
																								normalizedValue === 'address' &&
																								[
																									'attendeeAddress',
																									'attendeeDefined',
																									'attendeeInPerson',
																								].includes(currentLocation)
																							) {
																								return Promise.reject(
																									new Error(
																										'Address field will be automatically asked for in-person meetings',
																									),
																								);
																							}
																							if (
																								normalizedValue === 'phone' &&
																								currentLocation === 'attendeePhone'
																							) {
																								return Promise.reject(
																									new Error(
																										'Phone field will be automatically asked for phone meetings',
																									),
																								);
																							}

																							return Promise.resolve();
																						},
																					},
																				]}
																				className="mb-0"
																			>
																				<Input
																					placeholder="Field Name (e.g., Company Name)"
																					className="field-name-input"
																				/>
																			</Form.Item>
																			<div className="flex items-center">
																				<Form.Item
																					{...restField}
																					name={[name, 'description']}
																					rules={[
																						{ required: true, message: 'Description is required' },
																					]}
																					className="mb-0 flex-grow"
																				>
																					<Input
																						placeholder="Description (e.g., What is your company name?)"
																						className="field-description-input"
																					/>
																				</Form.Item>
																				<MinusCircleOutlined
																					onClick={() => remove(name)}
																					className="remove-field-button ml-2 flex-shrink-0"
																				/>
																			</div>
																		</div>
																	))}
																</div>
															) : (
																<div className="no-fields-message text-gray-500 italic mb-4 text-center py-4">
																	No custom fields added yet
																</div>
															)}
															<Form.Item className="add-field-button-container">
																<Button
																	type="dashed"
																	onClick={() => add()}
																	block
																	icon={<PlusOutlined />}
																	className="add-field-button"
																>
																	Add Custom Field
																</Button>
															</Form.Item>
														</div>
													)}
												</Form.List>
											</div>
										)}
									</React.Fragment>
								))}
							</>
						)}
					</Form.List>

					<div className="step-actions">
						<Button onClick={onCancel} className="cancel-button">
							Cancel
						</Button>
						<Button type="primary" onClick={handleSubmit} loading={loading} className="save-button">
							Save Changes
						</Button>
					</div>
				</div>
			</Form>
		</div>
	);
};
